import {
  ChamberMemberProAccountAllocationTypeEnum,
  ChamberMemberProCurrencyEnum,
  ChamberMemberProEmailRecipientRoutingModeEnum,
  ChamberMemberProInvoicePaymentTermsEnum,
  ChamberMemberProManualPaymentMethodTypeEnum,
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProMembershipPricingModelEnum,
  ChamberMemberProPaymentBeforeApprovalRuleEnum,
  ChamberMemberProPaymentScheduleEnum,
  ChamberMemberProSettingsInput,
  GetChamberMemberProSettingsQuery,
  MonthEnum,
} from "../../../../graphql/types";
import {
  ChamberSettingsFormState,
  MembershipEmployeeRange,
  MembershipModel,
  PaymentBeforeApprovalRule,
  PaymentMethod,
  accountAllocationRows,
  defaultAccountAllocations,
  defaultChamberGroupInsuranceBillingDetails,
  defaultChamberGroupInsurancePaymentSettings,
  defaultEventTicketCreditCardPaymentMode,
  defaultMembershipApplicationCommunicationOptions,
  defaultMembershipDiscountSettings,
  defaultOneTimeOnboardingFeeSettings,
  defaultProrationMonths,
  defaultSenderDomainSettings,
  defaultStripeConnectSettings,
  emailAutomations,
} from "./settingsData";

type ApiSettings = GetChamberMemberProSettingsQuery["getChamberMemberProSettings"];

const currencyLabelByEnum: Record<ChamberMemberProCurrencyEnum, string> = {
  [ChamberMemberProCurrencyEnum.Cad]: "CAD - Canadian Dollar (CAD)",
  [ChamberMemberProCurrencyEnum.Usd]: "USD - United States Dollar (USD)",
};

const currencyEnumByLabel: Record<string, ChamberMemberProCurrencyEnum> = {
  "CAD - Canadian Dollar": ChamberMemberProCurrencyEnum.Cad,
  "CAD - Canadian Dollar (CAD)": ChamberMemberProCurrencyEnum.Cad,
  "USD - United States Dollar": ChamberMemberProCurrencyEnum.Usd,
  "USD - United States Dollar (USD)": ChamberMemberProCurrencyEnum.Usd,
};

const monthLabelByEnum: Record<MonthEnum, string> = {
  [MonthEnum.January]: "January",
  [MonthEnum.February]: "February",
  [MonthEnum.March]: "March",
  [MonthEnum.April]: "April",
  [MonthEnum.May]: "May",
  [MonthEnum.June]: "June",
  [MonthEnum.July]: "July",
  [MonthEnum.August]: "August",
  [MonthEnum.September]: "September",
  [MonthEnum.October]: "October",
  [MonthEnum.November]: "November",
  [MonthEnum.December]: "December",
};

const monthEnumByLabel = Object.entries(monthLabelByEnum).reduce(
  (acc, [monthEnum, label]) => ({
    ...acc,
    [label]: monthEnum as MonthEnum,
  }),
  {} as Record<string, MonthEnum>
);

const monthEnumByShortLabel: Record<string, MonthEnum> = {
  Jan: MonthEnum.January,
  Feb: MonthEnum.February,
  Mar: MonthEnum.March,
  Apr: MonthEnum.April,
  May: MonthEnum.May,
  Jun: MonthEnum.June,
  Jul: MonthEnum.July,
  Aug: MonthEnum.August,
  Sep: MonthEnum.September,
  Oct: MonthEnum.October,
  Nov: MonthEnum.November,
  Dec: MonthEnum.December,
};

const manualPaymentMethodIdAliases: Record<string, string> = {
  cash: ChamberMemberProManualPaymentMethodTypeEnum.Cash,
  "e-transfer": ChamberMemberProManualPaymentMethodTypeEnum.ETransfer,
  "direct-deposit": ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
  cheque: ChamberMemberProManualPaymentMethodTypeEnum.Cheque,
  "invoice-me": ChamberMemberProManualPaymentMethodTypeEnum.InvoiceMe,
};

const normalizeManualPaymentMethodIdForSettings = (methodId: string) => {
  const trimmedMethodId = methodId.trim();

  return (
    manualPaymentMethodIdAliases[trimmedMethodId] ||
    manualPaymentMethodIdAliases[trimmedMethodId.toLowerCase()] ||
    trimmedMethodId
  );
};

const normalizeManualPaymentMethodsForSettings = (
  manualPaymentMethods: PaymentMethod[]
): PaymentMethod[] =>
  manualPaymentMethods.reduce<PaymentMethod[]>((normalizedMethods, method) => {
    const normalizedMethod = {
      ...method,
      id: normalizeManualPaymentMethodIdForSettings(method.id),
    };
    const existingMethodIndex = normalizedMethods.findIndex(
      (existingMethod) => existingMethod.id === normalizedMethod.id
    );

    if (existingMethodIndex === -1) {
      return [...normalizedMethods, normalizedMethod];
    }

    return normalizedMethods.map((existingMethod, index) =>
      index === existingMethodIndex ? normalizedMethod : existingMethod
    );
  }, []);

const shortMonthLabelByEnum = Object.entries(monthEnumByShortLabel).reduce(
  (acc, [shortLabel, monthEnum]) => ({
    ...acc,
    [monthEnum]: shortLabel,
  }),
  {} as Record<MonthEnum, string>
);

const pricingModelByEnum: Record<
  ChamberMemberProMembershipPricingModelEnum,
  MembershipModel["pricingModel"]
> = {
  [ChamberMemberProMembershipPricingModelEnum.Fixed]: "fixed",
  [ChamberMemberProMembershipPricingModelEnum.EmployeeRange]: "employeeRange",
};

const pricingModelEnumByUi: Record<
  MembershipModel["pricingModel"],
  ChamberMemberProMembershipPricingModelEnum
> = {
  fixed: ChamberMemberProMembershipPricingModelEnum.Fixed,
  employeeRange: ChamberMemberProMembershipPricingModelEnum.EmployeeRange,
};

const paymentResponsibilityByEnum: Record<
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  MembershipModel["paymentResponsibility"]
> = {
  [ChamberMemberProMembershipModelPaymentResponsibilityEnum.MemberPays]:
    "Member/applicant pays",
  [ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays]:
    "Third party pays the chamber",
  [ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge]:
    "No charge",
};

const paymentResponsibilityEnumByUi: Record<
  MembershipModel["paymentResponsibility"],
  ChamberMemberProMembershipModelPaymentResponsibilityEnum
> = {
  "Member/applicant pays":
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.MemberPays,
  "Third party pays the chamber":
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays,
  "No charge":
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge,
};

const paymentBeforeApprovalRuleByEnum: Record<
  ChamberMemberProPaymentBeforeApprovalRuleEnum,
  PaymentBeforeApprovalRule
> = {
  [ChamberMemberProPaymentBeforeApprovalRuleEnum.AllowPaymentBeforeApproval]:
    "Allow payment before approval",
  [ChamberMemberProPaymentBeforeApprovalRuleEnum.CollectPaymentAfterApproval]:
    "Collect payment after approval",
};

const paymentBeforeApprovalRuleEnumByUi: Record<
  PaymentBeforeApprovalRule,
  ChamberMemberProPaymentBeforeApprovalRuleEnum
> = {
  "Allow payment before approval":
    ChamberMemberProPaymentBeforeApprovalRuleEnum.AllowPaymentBeforeApproval,
  "Collect payment after approval":
    ChamberMemberProPaymentBeforeApprovalRuleEnum.CollectPaymentAfterApproval,
};

export const formatDollarAmount = (value: number): string =>
  `$${value.toFixed(2)}`;

const parseNumber = (value: string): number => {
  const parsedValue = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return isNaN(parsedValue) ? 0 : parsedValue;
};

const roundDollarAmount = (value: number): number =>
  Math.round(value * 100) / 100;

export const getDerivedMonthlyMembershipPriceInDollars = (
  annualPriceInDollars: number
): number =>
  Number.isFinite(annualPriceInDollars) && annualPriceInDollars > 0
    ? roundDollarAmount(annualPriceInDollars / 12)
    : 0;

export const getDerivedMonthlyMembershipPrice = (
  annualPrice: string
): string =>
  formatDollarAmount(
    getDerivedMonthlyMembershipPriceInDollars(parseNumber(annualPrice))
  );

const formatNumber = (value: number): string => `${value}`;

const mapAdditionalCostsFromApi = (
  additionalCosts: ApiSettings["membership"]["membershipModels"][number]["additionalCosts"]
) =>
  additionalCosts.map((cost) => ({
    id: cost.id,
    code: cost.code || "",
    name: cost.name,
    enabled: cost.enabled,
    annualAmount: formatDollarAmount(cost.annualAmountInDollars),
    monthlyAmount: formatDollarAmount(cost.monthlyAmountInDollars),
    accountNumber: cost.accountNumber || "",
    description: cost.description,
  }));

const mapAdditionalCostsToApi = (model: MembershipModel) =>
  model.additionalCosts.map((cost) => ({
    id: cost.id,
    code: cost.code.trim(),
    name: cost.name,
    enabled: cost.enabled,
    annualAmountInDollars: parseNumber(cost.annualAmount),
    monthlyAmountInDollars: parseNumber(cost.monthlyAmount),
    accountNumber: cost.accountNumber.trim(),
    description: cost.description,
  }));

const mapInvoiceTaxCategoryFromApi = (
  category:
    | ApiSettings["paymentsAndTaxes"]["invoiceTaxSettings"]["membershipInvoices"]
    | undefined
) => ({
  enabled: !!category?.enabled,
  firstTaxRateLabel: category?.firstTaxRateLabel || "",
  firstTaxRatePercentage: formatNumber(category?.firstTaxRatePercentage || 0),
  secondTaxRateLabel: category?.secondTaxRateLabel || "",
  secondTaxRatePercentage: formatNumber(category?.secondTaxRatePercentage || 0),
});

const mapInvoiceTaxCategoryToApi = (
  category: ChamberSettingsFormState["paymentsAndTaxes"]["invoiceTaxSettings"]["membershipInvoices"]
) => ({
  enabled: category.enabled,
  firstTaxRateLabel: category.firstTaxRateLabel.trim(),
  firstTaxRatePercentage: parseNumber(category.firstTaxRatePercentage),
  secondTaxRateLabel: category.secondTaxRateLabel.trim(),
  secondTaxRatePercentage: parseNumber(category.secondTaxRatePercentage),
});

const accountAllocationLabelByType = accountAllocationRows.reduce(
  (acc, row) => ({
    ...acc,
    [row.type]: row.label,
  }),
  {} as Record<ChamberMemberProAccountAllocationTypeEnum, string>
);

const mapAccountAllocationsFromApi = (
  accountAllocations:
    | ApiSettings["paymentsAndTaxes"]["accountAllocations"]
    | undefined
) => {
  const defaultRows = defaultAccountAllocations();
  const rowByType = new Map(
    (accountAllocations || []).map((row) => [row.type, row])
  );

  return defaultRows.map((defaultRow) => {
    const row = rowByType.get(defaultRow.type);

    return {
      ...defaultRow,
      label: accountAllocationLabelByType[defaultRow.type] || defaultRow.label,
      accountNumber: row?.accountNumber || "",
      accountName: row?.accountName || "",
    };
  });
};

const mapAccountAllocationsToApi = (
  accountAllocations: ChamberSettingsFormState["paymentsAndTaxes"]["accountAllocations"]
) =>
  accountAllocations.map((accountAllocation) => ({
    type: accountAllocation.type,
    accountNumber: accountAllocation.accountNumber.trim(),
    accountName: accountAllocation.accountName.trim(),
  }));

const mapEnabledPaymentSchedulesFromApi = (
  enabledPaymentSchedules: ChamberMemberProPaymentScheduleEnum[]
) => ({
  annual:
    enabledPaymentSchedules.includes(ChamberMemberProPaymentScheduleEnum.Annual),
  monthly:
    enabledPaymentSchedules.includes(ChamberMemberProPaymentScheduleEnum.Monthly),
});

const mapEnabledPaymentSchedulesToApi = (
  enabledPaymentSchedules: ChamberSettingsFormState["membership"]["enabledPaymentSchedules"]
) => {
  const schedules: ChamberMemberProPaymentScheduleEnum[] = [];

  if (enabledPaymentSchedules.annual) {
    schedules.push(ChamberMemberProPaymentScheduleEnum.Annual);
  }

  if (enabledPaymentSchedules.monthly) {
    schedules.push(ChamberMemberProPaymentScheduleEnum.Monthly);
  }

  return schedules.length > 0
    ? schedules
    : [
        ChamberMemberProPaymentScheduleEnum.Annual,
        ChamberMemberProPaymentScheduleEnum.Monthly,
      ];
};

const mapProratedPricesFromApi = (
  proratedSignupPrices: ApiSettings["membership"]["proratedSignupPrices"]
) =>
  proratedSignupPrices.reduce((acc, proratedPrice) => {
    const month = shortMonthLabelByEnum[proratedPrice.month];

    return {
      ...acc,
      [proratedPrice.pricingKey]: {
        ...(acc[proratedPrice.pricingKey] || {}),
        [month]: formatDollarAmount(proratedPrice.priceInDollars),
      },
    };
  }, {} as Record<string, Record<string, string>>);

const getProratedPricingItemsForMembershipModel = (
  membershipModel: MembershipModel
): { pricingKey: string; annualPrice: string }[] => {
  if (membershipModel.pricingModel === "employeeRange") {
    return membershipModel.ranges.map((range) => ({
      pricingKey: `${membershipModel.id}-${range.id}`,
      annualPrice: range.annualPrice,
    }));
  }

  return [
    {
      pricingKey: membershipModel.id,
      annualPrice: membershipModel.annualPrice,
    },
  ];
};

const getProratedSignupPriceFallback = (
  annualPrice: string,
  monthIndex: number
) => (parseNumber(annualPrice) * (12 - monthIndex)) / 12;

const getProratedSignupPriceForSave = ({
  annualPrice,
  monthIndex,
  savedPrice,
}: {
  annualPrice: string;
  monthIndex: number;
  savedPrice?: string;
}) => {
  const fallbackPrice = getProratedSignupPriceFallback(annualPrice, monthIndex);

  if (!savedPrice) {
    return fallbackPrice;
  }

  return parseNumber(savedPrice);
};

const tierModelIdPattern = /^tier-[1-6]$/;

const isTierMembershipModel = (membershipModel: MembershipModel) =>
  tierModelIdPattern.test(membershipModel.id);

const getTierNumber = (membershipModel: MembershipModel) =>
  Number(membershipModel.id.replace("tier-", ""));

const isCompanyMembershipModel = (membershipModel: MembershipModel) =>
  membershipModel.id === "company" ||
  membershipModel.pricingModel === "employeeRange";

export const getMembershipEmployeeRangeCode = ({
  from,
  to,
}: {
  from: string;
  to: string;
}, {
  openEnded = false,
}: {
  openEnded?: boolean;
} = {}) => {
  const trimmedFrom = from.trim();
  const trimmedTo = to.trim();

  if (openEnded) {
    return trimmedFrom ? `CM${trimmedFrom}+` : "";
  }

  if (trimmedTo) {
    return `CM${trimmedFrom || "0"}-${trimmedTo}`;
  }

  return trimmedFrom ? `CM${trimmedFrom}` : "";
};

export const isAutoMembershipEmployeeRangeCode = (
  range: Pick<MembershipEmployeeRange, "code" | "from" | "to">
) => {
  const trimmedCode = range.code.trim();

  return (
    !trimmedCode ||
    trimmedCode === getMembershipEmployeeRangeCode(range) ||
    trimmedCode === getMembershipEmployeeRangeCode(range, { openEnded: true })
  );
};

export const isFinalMembershipEmployeeRange = (
  ranges: MembershipEmployeeRange[],
  index: number
) => index === ranges.length - 1;

export const normalizeFinalMembershipEmployeeRange = (
  range: MembershipEmployeeRange
): MembershipEmployeeRange => ({
  ...range,
  to: "",
  code: isAutoMembershipEmployeeRangeCode(range)
    ? getMembershipEmployeeRangeCode({ ...range, to: "" }, { openEnded: true })
    : range.code,
});

export const normalizeFormerFinalMembershipEmployeeRange = (
  range: MembershipEmployeeRange
): MembershipEmployeeRange => ({
  ...range,
  code: isAutoMembershipEmployeeRangeCode(range)
    ? getMembershipEmployeeRangeCode(range)
    : range.code,
});

export const normalizeMembershipEmployeeRangesForForm = (
  ranges: MembershipEmployeeRange[]
) =>
  ranges.map((range, index, allRanges) =>
    isFinalMembershipEmployeeRange(allRanges, index)
      ? normalizeFinalMembershipEmployeeRange(range)
      : normalizeFormerFinalMembershipEmployeeRange(range)
  );

const normalizeMembershipModelMonthlyPrice = (
  membershipModel: MembershipModel
): MembershipModel => ({
  ...membershipModel,
  monthlyPrice: getDerivedMonthlyMembershipPrice(membershipModel.annualPrice),
  ranges: membershipModel.ranges.map((range) => ({
    ...range,
    monthlyPrice: getDerivedMonthlyMembershipPrice(range.annualPrice),
  })),
});

export const normalizeMembershipModelsForForm = (
  membershipModels: MembershipModel[]
) =>
  membershipModels.map((membershipModel) =>
    normalizeMembershipModelMonthlyPrice(
      membershipModel.pricingModel === "employeeRange"
        ? {
            ...membershipModel,
            ranges: normalizeMembershipEmployeeRangesForForm(
              membershipModel.ranges
            ),
          }
        : membershipModel
    )
  );

const shouldIncludeMembershipModelForProratedPricing = (
  membershipModel: MembershipModel
) =>
  membershipModel.enabled &&
  membershipModel.publicMembershipApplicationEnabled;

const normalizeMembershipModelsForSave = (
  membershipModels: MembershipModel[]
): MembershipModel[] => {
  const normalizedMembershipModels =
    normalizeMembershipModelsForForm(membershipModels);
  const hasEnabledPublicModel = normalizedMembershipModels.some(
    (model) => model.enabled && model.publicMembershipApplicationEnabled
  );

  if (hasEnabledPublicModel) {
    return normalizedMembershipModels.map((model) => ({
      ...model,
      publicMembershipApplicationEnabled: model.enabled
        ? model.publicMembershipApplicationEnabled
        : false,
    }));
  }

  const actualTierMembershipModels = normalizedMembershipModels.filter(
    isTierMembershipModel
  );
  const enabledNonTierModelExists = normalizedMembershipModels.some(
    (model) => !isTierMembershipModel(model) && model.enabled
  );
  const tierSetupActive =
    actualTierMembershipModels.some((model) => model.enabled) ||
    (actualTierMembershipModels.length > 0 && !enabledNonTierModelExists);

  if (tierSetupActive) {
    return normalizedMembershipModels.map((model) => {
      const enabledTier =
        isTierMembershipModel(model) && getTierNumber(model) <= 3;

      return {
        ...model,
        enabled: enabledTier,
        publicMembershipApplicationEnabled: enabledTier,
      };
    });
  }

  const primaryNonTierModel =
    normalizedMembershipModels.find(isCompanyMembershipModel) ||
    normalizedMembershipModels.find((model) => !isTierMembershipModel(model));

  return normalizedMembershipModels.map((model) => {
    if (isTierMembershipModel(model)) {
      return {
        ...model,
        enabled: false,
        publicMembershipApplicationEnabled: false,
      };
    }

    const enabled = model.enabled || model.id === primaryNonTierModel?.id;

    return {
      ...model,
      enabled,
      publicMembershipApplicationEnabled: enabled,
    };
  });
};

const mapProratedPricesToApi = (
  membership: ChamberSettingsFormState["membership"]
) => {
  if (!membership.globalToggleState.prorate || !membership.enabledPaymentSchedules.annual) {
    return [];
  }

  return membership.membershipModels
    .filter(shouldIncludeMembershipModelForProratedPricing)
    .flatMap((membershipModel) =>
      getProratedPricingItemsForMembershipModel(membershipModel).flatMap(
        ({ pricingKey, annualPrice }) =>
          defaultProrationMonths.map((month, monthIndex) => ({
            pricingKey,
            month: monthEnumByShortLabel[month.month],
            priceInDollars: getProratedSignupPriceForSave({
              annualPrice,
              monthIndex,
              savedPrice: membership.proratedPrices[pricingKey]?.[month.month],
            }),
          }))
      )
    );
};

const DEFAULT_EMAIL_TEMPLATE_UPDATED_AT_UTC_MILLI = Date.UTC(
  2026,
  4,
  28,
  21,
  16,
  0
);
const EMAIL_TEMPLATE_TIMESTAMP_OFFSET_UTC_MILLI = 60 * 1000;
const EMAIL_TEMPLATE_TIMESTAMP_ORDER = [
  "membership-confirmation",
  "payment-received",
  "membership-approved",
  "membership-declined",
  "renewal-reminder",
  "event-registration-acknowledgement",
  "event-invoice-me-payment-request",
  "event-payment-receipt",
  "event-24-hour-reminder",
  "event-sponsorship-request-received",
  "event-sponsorship-approval-payment-request",
  "event-sponsorship-rejection",
];
const emailTemplateTimestampOrderById = EMAIL_TEMPLATE_TIMESTAMP_ORDER.reduce(
  (acc, templateId, index) => ({
    ...acc,
    [templateId]: index,
  }),
  {} as Record<string, number>
);

const getDefaultEmailTemplateUpdatedAtUtcMilli = (index: number) =>
  DEFAULT_EMAIL_TEMPLATE_UPDATED_AT_UTC_MILLI +
  index * EMAIL_TEMPLATE_TIMESTAMP_OFFSET_UTC_MILLI;

const getEmailTemplateTimestampIndex = ({
  fallbackIndex,
  templateId,
}: {
  fallbackIndex: number;
  templateId: string;
}) =>
  typeof emailTemplateTimestampOrderById[templateId] === "number"
    ? emailTemplateTimestampOrderById[templateId]
    : fallbackIndex;

const getValidUpdatedAtUtcMilliOrNull = (updatedAtUtcMilli?: number | null) =>
  typeof updatedAtUtcMilli === "number" &&
  Number.isFinite(updatedAtUtcMilli) &&
  updatedAtUtcMilli > 0
    ? updatedAtUtcMilli
    : null;

const defaultEmailTemplates = (): ChamberSettingsFormState["email"]["emailTemplates"] =>
  emailAutomations.map(({ preview, ...automation }, index) => ({
    ...automation,
    body: preview,
    senderEmail: null,
    updatedAtUtcMilli: getDefaultEmailTemplateUpdatedAtUtcMilli(
      getEmailTemplateTimestampIndex({
        fallbackIndex: index,
        templateId: automation.id,
      })
    ),
  }));

const legacyDefaultEmailSubjectsById: Record<string, string> = {
  "membership-confirmation":
    "We received your {chamberName} membership application",
  "payment-received": "Payment received for invoice {invoiceNumber}",
  "membership-approved": "Your {chamberName} membership has been approved",
  "membership-declined": "Update on your {chamberName} membership application",
  "renewal-reminder": "Your {chamberName} membership renews on {renewalDate}",
  "event-registration-acknowledgement":
    "We received your registration for {eventName}",
  "event-invoice-me-payment-request": "Payment instructions for {eventName}",
  "event-payment-receipt": "Payment received for {eventName}",
  "event-sponsorship-request-received":
    "We received your sponsorship request for {eventName}",
  "event-sponsorship-approval-payment-request":
    "Your sponsorship for {eventName} has been approved",
  "event-sponsorship-rejection":
    "Update on your sponsorship request for {eventName}",
  "event-24-hour-reminder": "Reminder: {eventName} is tomorrow",
};

const mergeEmailTemplatesWithDefaults = (
  templates: ChamberSettingsFormState["email"]["emailTemplates"]
) => {
  const mergedTemplates = new Map(
    defaultEmailTemplates().map((template) => [template.id, template])
  );

  templates.forEach((template) => {
    const defaultTemplate = mergedTemplates.get(template.id);
    const legacySubject = legacyDefaultEmailSubjectsById[template.id];
    const subject =
      legacySubject && template.subject === legacySubject
        ? defaultTemplate?.subject || template.subject
        : template.subject;

    mergedTemplates.set(template.id, {
      ...defaultTemplate,
      ...template,
      subject,
      category: template.category || defaultTemplate?.category,
      updatedAtUtcMilli:
        getValidUpdatedAtUtcMilliOrNull(template.updatedAtUtcMilli) ||
        getValidUpdatedAtUtcMilliOrNull(defaultTemplate?.updatedAtUtcMilli) ||
        null,
    });
  });

  return Array.from(mergedTemplates.values());
};

export const mapApiSettingsToFormState = (
  settings: ApiSettings
): ChamberSettingsFormState => ({
  setup: {
    fiscalYearStartMonth: monthLabelByEnum[settings.setup.fiscalYearStartMonth],
    fiscalYearStartDay: String(settings.setup.fiscalYearStartDay),
    currency: currencyLabelByEnum[settings.setup.currency],
    timeZone: settings.setup.timeZone,
    membershipRenewalFixedDateEnabled:
      settings.setup.membershipRenewalFixedDateEnabled,
    membershipRenewalFixedDateMonth:
      monthLabelByEnum[settings.setup.membershipRenewalFixedDateMonth],
    membershipRenewalFixedDateDay: String(
      settings.setup.membershipRenewalFixedDateDay
    ),
    membershipApplicationCommunicationOptions: {
      emailEnabled:
        settings.setup.membershipApplicationCommunicationOptions?.emailEnabled ??
        defaultMembershipApplicationCommunicationOptions.emailEnabled,
      phoneEnabled:
        settings.setup.membershipApplicationCommunicationOptions?.phoneEnabled ??
        defaultMembershipApplicationCommunicationOptions.phoneEnabled,
      textMessageEnabled:
        settings.setup.membershipApplicationCommunicationOptions
          ?.textMessageEnabled ??
        defaultMembershipApplicationCommunicationOptions.textMessageEnabled,
    },
  },
  membership: {
    renewalNoticeDays: String(settings.membership.renewalNoticeDays),
    autoRenewalNoticeDays: String(settings.membership.autoRenewalNoticeDays),
    renewalReminderOneDays: String(settings.membership.renewalReminderOneDays),
    renewalReminderTwoDays: String(settings.membership.renewalReminderTwoDays),
    enabledPaymentSchedules: mapEnabledPaymentSchedulesFromApi(
      settings.membership.enabledPaymentSchedules
    ),
    partTimeEmployeesPerFullTime: String(
      settings.membership.partTimeEmployeesPerFullTime
    ),
    memberBenefits: settings.membership.memberBenefits.map((benefit) => ({
      id: benefit.id,
      label: benefit.label,
    })),
    membershipModels: normalizeMembershipModelsForForm(
      [...settings.membership.membershipModels]
        .sort((firstModel, secondModel) => {
          if (firstModel.position !== secondModel.position) {
            return firstModel.position - secondModel.position;
          }

          return firstModel.name.localeCompare(secondModel.name);
        })
        .map((model) => ({
          id: model.id,
          code: model.code,
          name: model.name,
          description: model.description,
          memberTarget: model.memberTarget || 0,
          enabled: model.enabled,
          publicMembershipApplicationEnabled:
            model.publicMembershipApplicationEnabled,
          position: model.position,
          paymentResponsibility:
            paymentResponsibilityByEnum[model.paymentResponsibility],
          thirdPartyPayerName: model.thirdPartyPayerName || "",
          thirdPartyPaymentNotes: model.thirdPartyPaymentNotes || "",
          pricingModel: pricingModelByEnum[model.pricingModel],
          annualPrice: formatDollarAmount(model.annualPriceInDollars),
          monthlyPrice: formatDollarAmount(model.monthlyPriceInDollars),
          includedBenefitIds: model.includedBenefitIds,
          additionalCosts: mapAdditionalCostsFromApi(model.additionalCosts),
          ranges: model.ranges.map((range) => ({
            id: range.id,
            code: range.code,
            from: String(range.from),
            to:
              range.to === null || range.to === undefined
                ? ""
                : String(range.to),
            annualPrice: formatDollarAmount(range.annualPriceInDollars),
            monthlyPrice: formatDollarAmount(range.monthlyPriceInDollars),
          })),
        }))
    ),
    proratedPrices: mapProratedPricesFromApi(
      settings.membership.proratedSignupPrices
    ),
    globalToggleState: {
      prorate: settings.membership.proratedSignupPricingEnabled,
      autoRenewal: settings.membership.allowAutoRenewal,
      boardApproval: settings.membership.requireApproval,
      autoApproveRenewals: true,
    },
    paymentBeforeApprovalRule:
      paymentBeforeApprovalRuleByEnum[
        settings.membership.paymentBeforeApprovalRule
      ],
  },
  paymentsAndTaxes: {
    taxJurisdictionId: settings.paymentsAndTaxes.taxJurisdictionId,
    taxRegistrationNumber: settings.paymentsAndTaxes.taxRegistrationNumber || "",
    invoiceStartNumber: settings.paymentsAndTaxes.invoiceStartNumber || "",
    cardProcessingFee: {
      enabled: settings.paymentsAndTaxes.cardProcessingFee.enabled,
      percentage: String(settings.paymentsAndTaxes.cardProcessingFee.percentage),
      fixedAmount: formatDollarAmount(
        settings.paymentsAndTaxes.cardProcessingFee.fixedAmountInDollars
      ),
    },
    manualPaymentMethods: normalizeManualPaymentMethodsForSettings(
      settings.paymentsAndTaxes.manualPaymentMethods.map(
        (method): PaymentMethod => ({
          id: method.id,
          label: method.label,
          description: method.description,
          enabled: method.enabled,
          emailSubject: method.emailSubject,
          emailBody: method.emailBody,
        })
      )
    ),
    provincialChamberFee: {
      enabled: settings.paymentsAndTaxes.provincialChamberFee.enabled,
      amount: formatDollarAmount(
        settings.paymentsAndTaxes.provincialChamberFee.amountInDollars
      ),
      showSeparateOnInvoice:
        settings.paymentsAndTaxes.provincialChamberFee.showSeparateOnInvoice,
    },
    invoiceTaxSettings: {
      membershipInvoices: mapInvoiceTaxCategoryFromApi(
        settings.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices
      ),
      sponsorshipInvoices: mapInvoiceTaxCategoryFromApi(
        settings.paymentsAndTaxes.invoiceTaxSettings.sponsorshipInvoices
      ),
      eventTicketsInvoices: mapInvoiceTaxCategoryFromApi(
        settings.paymentsAndTaxes.invoiceTaxSettings.eventTicketsInvoices
      ),
      advertisingInvoices: mapInvoiceTaxCategoryFromApi(
        settings.paymentsAndTaxes.invoiceTaxSettings.advertisingInvoices
      ),
    },
    monthlyMembershipAdministrativeFee: {
      enabled:
        settings.paymentsAndTaxes.monthlyMembershipAdministrativeFee.enabled,
      amount: formatDollarAmount(
        settings.paymentsAndTaxes.monthlyMembershipAdministrativeFee
          .amountInDollars
      ),
    },
    membershipDiscount:
      settings.paymentsAndTaxes.membershipDiscount ||
      defaultMembershipDiscountSettings,
    oneTimeOnboardingFee: {
      enabled:
        settings.paymentsAndTaxes.oneTimeOnboardingFee?.enabled ??
        defaultOneTimeOnboardingFeeSettings.enabled,
      code:
        settings.paymentsAndTaxes.oneTimeOnboardingFee?.code ||
        defaultOneTimeOnboardingFeeSettings.code,
      description:
        settings.paymentsAndTaxes.oneTimeOnboardingFee?.description ||
        defaultOneTimeOnboardingFeeSettings.description,
      amount: formatDollarAmount(
        settings.paymentsAndTaxes.oneTimeOnboardingFee?.amountInDollars || 0
      ),
    },
    eventTicketCreditCardPaymentMode:
      settings.paymentsAndTaxes.eventTicketCreditCardPaymentMode ||
      defaultEventTicketCreditCardPaymentMode,
    accountAllocations: mapAccountAllocationsFromApi(
      settings.paymentsAndTaxes.accountAllocations
    ),
    chamberGroupInsurancePayment: {
      enabled:
        settings.paymentsAndTaxes.chamberGroupInsurancePayment?.enabled ??
        defaultChamberGroupInsurancePaymentSettings.enabled,
      billingDetails: {
        ...defaultChamberGroupInsuranceBillingDetails,
        ...(settings.paymentsAndTaxes.chamberGroupInsurancePayment
          ?.billingDetails || {}),
      },
    },
    invoicePaymentTerms:
      settings.paymentsAndTaxes.invoicePaymentTerms ||
      ChamberMemberProInvoicePaymentTermsEnum.DueUponReceipt,
    overdueInvoiceLateFeePercentage: formatNumber(
      settings.paymentsAndTaxes.overdueInvoiceLateFeePercentage || 0
    ),
    stripeConnect:
      settings.paymentsAndTaxes.stripeConnect || defaultStripeConnectSettings,
  },
  email: {
    senderEmail: settings.email.senderEmail || "",
    replyToEmail: settings.email.replyToEmail || "",
    recipientRoutingMode:
      settings.email.recipientRoutingMode ||
      ChamberMemberProEmailRecipientRoutingModeEnum.TestInboxOverride,
    enabledSenderEmails: (settings.email.enabledSenderEmails || []).filter(
      Boolean
    ),
    senderDomain: settings.email.senderDomain || defaultSenderDomainSettings,
    emailTemplates: mergeEmailTemplatesWithDefaults(
      settings.email.emailTemplates.map((template) => ({
        id: template.id,
        title: template.title,
        description: template.description,
        enabled: template.enabled,
        subject: template.subject,
        body: template.body,
        senderEmail: template.senderEmail || null,
        updatedAtUtcMilli: template.updatedAtUtcMilli || null,
      }))
    ),
  },
  updatedAtUtcMilli: settings.updatedAtUtcMilli,
});

export const mapFormStateToApiSettingsInput = (
  settings: ChamberSettingsFormState
): ChamberMemberProSettingsInput => {
  const enabledPaymentSchedules = mapEnabledPaymentSchedulesToApi(
    settings.membership.enabledPaymentSchedules
  );
  const membershipModelsForSave = normalizeMembershipModelsForSave(
    settings.membership.membershipModels
  );
  const proratedSignupPricingEnabled =
    settings.membership.globalToggleState.prorate &&
    settings.membership.enabledPaymentSchedules.annual;

  return {
    setup: {
      fiscalYearStartMonth:
        monthEnumByLabel[settings.setup.fiscalYearStartMonth] ||
        MonthEnum.January,
      fiscalYearStartDay: parseNumber(settings.setup.fiscalYearStartDay),
      currency:
        currencyEnumByLabel[settings.setup.currency] ||
        ChamberMemberProCurrencyEnum.Cad,
      timeZone: settings.setup.timeZone,
      membershipRenewalFixedDateEnabled:
        settings.setup.membershipRenewalFixedDateEnabled,
      membershipRenewalFixedDateMonth:
        monthEnumByLabel[settings.setup.membershipRenewalFixedDateMonth] ||
        MonthEnum.January,
      membershipRenewalFixedDateDay: parseNumber(
        settings.setup.membershipRenewalFixedDateDay
      ),
      membershipApplicationCommunicationOptions:
        settings.setup.membershipApplicationCommunicationOptions,
    },
    membership: {
      enabledPaymentSchedules,
      renewalNoticeDays: parseNumber(settings.membership.renewalNoticeDays),
      autoRenewalNoticeDays: parseNumber(
        settings.membership.autoRenewalNoticeDays
      ),
      renewalReminderOneDays: parseNumber(
        settings.membership.renewalReminderOneDays
      ),
      renewalReminderTwoDays: parseNumber(
        settings.membership.renewalReminderTwoDays
      ),
      partTimeEmployeesPerFullTime: parseNumber(
        settings.membership.partTimeEmployeesPerFullTime
      ),
      allowAutoRenewal: settings.membership.globalToggleState.autoRenewal,
      requireApproval: settings.membership.globalToggleState.boardApproval,
      autoApproveRenewals: true,
      paymentBeforeApprovalRule:
        paymentBeforeApprovalRuleEnumByUi[
          settings.membership.paymentBeforeApprovalRule
        ],
      proratedSignupPricingEnabled,
      memberBenefits: settings.membership.memberBenefits.map((benefit) => ({
        id: benefit.id,
        label: benefit.label,
      })),
      membershipModels: membershipModelsForSave.map((model, index) => ({
        id: model.id,
        code: model.code,
        name: model.name,
        description: model.description,
        memberTarget: model.memberTarget,
        enabled: model.enabled,
        publicMembershipApplicationEnabled:
          model.publicMembershipApplicationEnabled,
        position:
          Number.isFinite(model.position) && model.position > 0
            ? model.position
            : index + 1,
        paymentResponsibility:
          paymentResponsibilityEnumByUi[model.paymentResponsibility],
        thirdPartyPayerName:
          model.paymentResponsibility === "Third party pays the chamber"
            ? model.thirdPartyPayerName.trim()
            : null,
        thirdPartyPaymentNotes:
          model.paymentResponsibility === "Third party pays the chamber"
            ? model.thirdPartyPaymentNotes.trim()
            : null,
        pricingModel: pricingModelEnumByUi[model.pricingModel],
        annualPriceInDollars: parseNumber(model.annualPrice),
        monthlyPriceInDollars: getDerivedMonthlyMembershipPriceInDollars(
          parseNumber(model.annualPrice)
        ),
        includedBenefitIds: model.includedBenefitIds,
        additionalCosts: mapAdditionalCostsToApi(model),
        ranges:
          model.pricingModel === "employeeRange"
            ? model.ranges.map((range, index) => ({
                id: range.id,
                code: range.code,
                from: parseNumber(range.from),
                to:
                  index === model.ranges.length - 1 && !range.to
                    ? null
                    : parseNumber(range.to),
                annualPriceInDollars: parseNumber(range.annualPrice),
                monthlyPriceInDollars: getDerivedMonthlyMembershipPriceInDollars(
                  parseNumber(range.annualPrice)
                ),
              }))
            : [],
      })),
      proratedSignupPrices: mapProratedPricesToApi({
        ...settings.membership,
        membershipModels: membershipModelsForSave,
      }),
    },
    paymentsAndTaxes: {
      taxJurisdictionId: settings.paymentsAndTaxes.taxJurisdictionId,
      taxRegistrationNumber: settings.paymentsAndTaxes.taxRegistrationNumber,
      invoiceStartNumber: settings.paymentsAndTaxes.invoiceStartNumber.trim(),
      cardProcessingFee: {
        enabled: settings.paymentsAndTaxes.cardProcessingFee.enabled,
        percentage: parseNumber(settings.paymentsAndTaxes.cardProcessingFee.percentage),
        fixedAmountInDollars: parseNumber(
          settings.paymentsAndTaxes.cardProcessingFee.fixedAmount
        ),
      },
      manualPaymentMethods: normalizeManualPaymentMethodsForSettings(
        settings.paymentsAndTaxes.manualPaymentMethods
      ).map((method) => ({
          id: method.id,
          label: method.label,
          description: method.description,
          enabled: method.enabled,
          emailSubject: method.emailSubject,
          emailBody: method.emailBody,
        })),
      provincialChamberFee: {
        enabled: settings.paymentsAndTaxes.provincialChamberFee.enabled,
        amountInDollars: parseNumber(
          settings.paymentsAndTaxes.provincialChamberFee.amount
        ),
        showSeparateOnInvoice:
          settings.paymentsAndTaxes.provincialChamberFee.showSeparateOnInvoice,
      },
      invoiceTaxSettings: {
        membershipInvoices: mapInvoiceTaxCategoryToApi(
          settings.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices
        ),
        sponsorshipInvoices: mapInvoiceTaxCategoryToApi(
          settings.paymentsAndTaxes.invoiceTaxSettings.sponsorshipInvoices
        ),
        eventTicketsInvoices: mapInvoiceTaxCategoryToApi(
          settings.paymentsAndTaxes.invoiceTaxSettings.eventTicketsInvoices
        ),
        advertisingInvoices: mapInvoiceTaxCategoryToApi(
          settings.paymentsAndTaxes.invoiceTaxSettings.advertisingInvoices
        ),
      },
      monthlyMembershipAdministrativeFee: {
        enabled:
          settings.paymentsAndTaxes.monthlyMembershipAdministrativeFee.enabled,
        amountInDollars: parseNumber(
          settings.paymentsAndTaxes.monthlyMembershipAdministrativeFee.amount
        ),
      },
      membershipDiscount: {
        enabled: settings.paymentsAndTaxes.membershipDiscount.enabled,
      },
      oneTimeOnboardingFee: {
        enabled: settings.paymentsAndTaxes.oneTimeOnboardingFee.enabled,
        code: settings.paymentsAndTaxes.oneTimeOnboardingFee.code.trim(),
        description:
          settings.paymentsAndTaxes.oneTimeOnboardingFee.description.trim(),
        amountInDollars: parseNumber(
          settings.paymentsAndTaxes.oneTimeOnboardingFee.amount
        ),
      },
      eventTicketCreditCardPaymentMode:
        settings.paymentsAndTaxes.eventTicketCreditCardPaymentMode,
      accountAllocations: mapAccountAllocationsToApi(
        settings.paymentsAndTaxes.accountAllocations
      ),
      chamberGroupInsurancePayment: {
        enabled: settings.paymentsAndTaxes.chamberGroupInsurancePayment.enabled,
        billingDetails: {
          companyName:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.companyName.trim(),
          streetAddress:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.streetAddress.trim(),
          city: settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.city.trim(),
          postalCode:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.postalCode.trim(),
          contactName:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.contactName.trim(),
          contactTitle:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.contactTitle.trim(),
          contactEmail:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.contactEmail.trim(),
          phoneNumber:
            settings.paymentsAndTaxes.chamberGroupInsurancePayment.billingDetails.phoneNumber.trim(),
        },
      },
      invoicePaymentTerms: settings.paymentsAndTaxes.invoicePaymentTerms,
      overdueInvoiceLateFeePercentage: parseNumber(
        settings.paymentsAndTaxes.overdueInvoiceLateFeePercentage
      ),
      stripeConnectMode: settings.paymentsAndTaxes.stripeConnect.mode,
    },
    email: {
      senderEmail: settings.email.senderEmail.trim(),
      replyToEmail: settings.email.replyToEmail.trim(),
      recipientRoutingMode: settings.email.recipientRoutingMode,
      enabledSenderEmails: settings.email.enabledSenderEmails
        .map((email) => email.trim())
        .filter(Boolean),
      emailTemplates: settings.email.emailTemplates.map((template) => ({
        id: template.id,
        title: template.title,
        description: template.description,
        enabled: template.enabled,
        subject: template.subject,
        body: template.body,
        senderEmail: template.senderEmail?.trim() || null,
        updatedAtUtcMilli: template.updatedAtUtcMilli || null,
      })),
    },
  };
};
